// client/src/pages/templates/AddTemplate.js

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch } from '../../api/api';
import { readScope } from '../../state/scopeStorage';
import TemplateFieldRow from './components/TemplateFieldRow';
import { useOrg } from "../orgs/org-context";

import {
  DndContext,
  closestCenter
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";

import '../../styles/template.css';
import '../../styles/Buttons.css';

function getActiveOrgIdSafe() {
  try {
    const s = readScope?.();
    return s?.org?.id || localStorage.getItem('activeOrgId') || localStorage.getItem('activeOrg') || null;
  } catch {
    try {
      return localStorage.getItem('activeOrgId') || localStorage.getItem('activeOrg') || null;
    } catch {
      return null;
    }
  }
}

function generateKey(label) {
  if (!label) return "field";

  const words = label
    .replace(/[^\w\s]/g, "")
    .trim()
    .split(/\s+/);

  const first = words.shift()?.toLowerCase() || "field";

  const rest = words.map(
    w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  );

  return [first, ...rest].join("");
}


export default function AddTemplate() {

  const navigate = useNavigate();
  const { activeOrgId } = useOrg();
  const initialOrgRef = useRef(activeOrgId);

  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);
  const [tpl, setTpl] = useState({
    name: '',
    description: '',
    fields: []
  });

  useEffect(() => {

    if (activeOrgId && initialOrgRef.current && activeOrgId !== initialOrgRef.current) {
      navigate("/templates/FieldTemplateList", { replace: true });
    }

  }, [activeOrgId, navigate]);


  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);


  const handleDragEnd = (event) => {

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setTpl((t) => {

      const oldIndex = t.fields.findIndex(f => f.id === active.id);
      const newIndex = t.fields.findIndex(f => f.id === over.id);

      return {
        ...t,
        fields: arrayMove(t.fields, oldIndex, newIndex)
      };

    });

  };


    const addField = () => {

      setTpl(t => ({
        ...t,
        fields: [
          ...t.fields,
          {
            id: crypto.randomUUID(),
            key: "",
            label: "",
            type: "text",
            options: [],
            autoFocus: true,   // ⭐ new flag
            mediaOptions: {
              type: "photo",
              maxVideoSeconds: 15
            }
          }
        ]
      }));

    };



    const removeField = (idx) => {

      setTpl(t => {

        const fields = [...t.fields];
        fields.splice(idx, 1);

        return { ...t, fields };

      });

    };



    const updateField = (idx, updatedField) => {

      setTpl(t => {

        const fields = [...t.fields];
        fields[idx] = updatedField;

        return { ...t, fields };

      });

    };


      const handleSave = async () => {

        try {

          setSaving(true);

          const fieldsClean = tpl.fields.map(f => {

            const clean = {
              key: f.key || generateKey(f.label),
              label: f.label,
              type: f.type,

              options: Array.isArray(f.options)
                ? f.options.map(v => v.trim()).filter(Boolean)
                : []
            };

            if (f.type === "media") {
            clean.mediaOptions = {
              type: f.mediaOptions?.type || "photo",
              maxVideoSeconds: f.mediaOptions?.maxVideoSeconds || 15
            };
            }

            return clean;

          });

          const body = {
            name: tpl.name,
            description: tpl.description,
            fields: fieldsClean
          };

          const created = await toast.promise(
            apiFetch(`/templates/create`, {
              method: "POST",
              body
            }),
            {
              pending: "Saving template…",
              success: "Template created.",
              error: "Save failed"
            }
          );

          if (created?._id) {
            navigate(`/templates/viewtemplate/${created._id}`);
          }

        } finally {

          setSaving(false);

        }

      };



  return (

    <div className="detail-page ">

      <div
        className="detail-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12
        }}
      >

        <h2>Add Template</h2>

        <div style={{ display: "flex", gap: 8 }}>

          <button className="btn-return" onClick={() => navigate("/templates/FieldTemplateList")}>
            Return
          </button>

          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Template"}
          </button>

        </div>

      </div>



      <div className="detail-card" style={{ background: "#eef2f7" }}>

        <div className="two-col-responsive">

        <label><b>Template Name</b></label>&nbsp;

        <input
          ref={nameRef}
          className="compact-input"
          value={tpl.name}
          onChange={e =>
            setTpl(t => ({ ...t, name: e.target.value }))
          }
        />&nbsp;&nbsp;&nbsp;&nbsp;


        <label><b>Description</b></label>&nbsp;

          <input
            className="compact-input"
            value={tpl.description}
            onChange={e =>
              setTpl(t => ({ ...t, description: e.target.value }))
            }
          />&nbsp;&nbsp;&nbsp;&nbsp;

          <button className="btn-save" onClick={addField}>
            Add Field
          </button>


        </div>



        <div className="table-scroll" style={{ background: "#fff" }}>

          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >

            <table className="compact-table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th style={{ width: 30 }}>#</th>
                  <th style={{ width: 50 }}>Label</th>
                  <th style={{ width: 50 }}>Type</th>
                  <th >Options</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>

              <SortableContext
                items={tpl.fields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >

                <tbody>

                  {tpl.fields.map((field, i) => (

                    <TemplateFieldRow
                      key={field.id}
                      id={field.id}
                      field={field}
                      index={i}
                      lastIndex={tpl.fields.length - 1}
                      addField={addField}
                      onUpdate={updateField}
                      onDelete={removeField}
                    />

                  ))}

                </tbody>

              </SortableContext>
            </table>

          </DndContext>

        </div>





      </div>

    </div>

  );

}
